import { useEffect, useRef, useState } from 'react';
import { EngranajeIcono } from './EngranajeIcono';

/**
 * El engranaje de la esquina: mi cuenta, y si el rol administra, equipo y
 * alertas.
 *
 * Antes «Equipo» y «Alertas» eran dos pestañas más en el nav principal, junto
 * a las vistas de datos (Procesos, Análisis…) — ocho pestañas para un admin,
 * que en una ventana angosta ni siquiera entraban en una fila. Configurar el
 * tenant y explorar sus datos son dos tareas distintas, y agruparlas acá,
 * detrás de un menú que no estorba cuando no se usa, es el patrón que ya
 * conoce cualquiera que haya usado Slack o GitHub. Auditoría se queda en el
 * nav principal a propósito: es una bitácora que se consulta, no algo que se
 * configura, aunque también sea solo para administradores.
 *
 * «Mi cuenta» sigue abriendo el mismo diálogo modal de siempre —cambiar
 * contraseña y administrar el segundo factor—, sin tocarlo: es un diálogo de
 * verdad (fondo, Escape, foco atrapado), no una sección de página, y forzarlo
 * a vivir dentro de esta lista habría sido pelear contra su propia forma.
 *
 * Solo el engranaje, sin el nombre ni el rol al lado: esos quedan como texto
 * aparte en el encabezado. Juntar un ícono de acción con una etiqueta de
 * identidad en el mismo botón mezclaba dos preguntas distintas —«quién soy» y
 * «dónde configuro»— detrás de un solo clic.
 */
export function MenuConfiguracion({
  puedeAdministrar,
  seccionActiva,
  onMiCuenta,
  onSeccion,
}: {
  puedeAdministrar: boolean;
  /** Si el nav ya está en «equipo» o «alertas», el engranaje se marca activo. */
  seccionActiva: 'equipo' | 'alertas' | null;
  onMiCuenta: () => void;
  onSeccion: (seccion: 'equipo' | 'alertas') => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const alHacerClicFuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', alHacerClicFuera);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', alHacerClicFuera);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierto]);

  function elegir(accion: () => void): void {
    setAbierto(false);
    accion();
  }

  return (
    <div className="menu-config" ref={contenedor}>
      <button
        className={`icono${seccionActiva ? ' activo' : ''}`}
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        aria-haspopup="true"
        title="Configuración"
      >
        <EngranajeIcono />
      </button>
      {abierto && (
        <div className="menu-config-lista" role="menu">
          <button role="menuitem" onClick={() => elegir(onMiCuenta)}>
            Mi cuenta
          </button>
          {puedeAdministrar && (
            <>
              <button role="menuitem" onClick={() => elegir(() => onSeccion('equipo'))}>
                Equipo
              </button>
              <button role="menuitem" onClick={() => elegir(() => onSeccion('alertas'))}>
                Alertas
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
